// Slide-over panel — productive replacement for centered Dialog. Slides
// in from the right on desktop, slides up as a bottom sheet on mobile.
//
// Desktop (>=640px): occupies a fixed-width column on the right, main
// app area STAYS interactive — no backdrop, just a subtle border +
// shadow. Esc + X button close.
//
// Mobile (<640px): full-width bottom sheet with rounded top corners,
// drag handle, scrollable content, and a backdrop overlay to dismiss.
// This avoids the awkward full-screen slide-in-right that caused zoom
// issues and conflicts with the sidebar drawer.

import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useOverlayStore } from '../../lib/overlay-store'
import { useDragToClose } from '../../lib/use-drag-to-close'
import { useIsMobile } from '../../lib/use-media-query'
import {
  useFocusScrollIntoView,
  useSheetMaxHeight,
  useVisualViewportRect,
} from '../../lib/use-visual-viewport'
import { cn } from '../../lib/utils'

interface SidePanelProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  /** Fixierter Bereich unter dem Titel (z.B. eine Tab-Leiste) — scrollt
   * NICHT mit dem Body mit und bleibt damit immer antippbar (D5). */
  mobileHeaderAction?: ReactNode
  mobileFullHeight?: boolean
  headerExtra?: ReactNode
  /** Tailwind width class for the panel. Defaults scale gently with
   * breakpoint so the panel always leaves ~half of the underlying
   * page visible: mobile = bottom sheet, tablet portrait ~ 50%, tablet
   * landscape ~ 47%, desktop ~ 30%. */
  width?: string
  /** Optional extra class on the panel surface itself. */
  className?: string
  /** Desktop-/Tablet-Zweig: dimmenden Backdrop rendern, der per Klick
   * schließt. Default false — Standard-SidePanels lassen die App bewusst
   * interaktiv (Erfassen-Flow). Fürs Kalender-Info-Panel <lg nötig, damit
   * Taps neben dem Panel nicht versehentlich im Grid landen. */
  desktopBackdrop?: boolean
}

export function SidePanel({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  headerExtra,
  mobileHeaderAction,
  mobileFullHeight = false,
  width = 'sm:w-[420px] md:w-[400px] lg:w-[480px] xl:w-[560px]',
  className,
  desktopBackdrop = false,
}: SidePanelProps) {
  const isMobile = useIsMobile()
  // Mobile-Sheet: maxHeight am SICHTBAREN Viewport (Tastatur!) statt 90vh;
  // fokussierte Felder im Scroll-Body sichtbar halten.
  const sheetMaxHeight = useSheetMaxHeight()
  const bodyRef = useFocusScrollIntoView<HTMLDivElement>(open && isMobile)
  // Portal-Wrapper am SICHTBAREN Viewport verankern, damit das
  // `bottom-0`-Sheet bei offener Tastatur über ihr bleibt (Footer/Felder
  // sonst dahinter — der Layout-Viewport schrumpft auf iOS nicht).
  const viewport = useVisualViewportRect()

  // Track this overlay in the global store so the AI bubble (and other
  // floating UI) can hide when a panel/sheet is covering the screen.
  const pushOverlay = useOverlayStore((s) => s.push)
  const popOverlay = useOverlayStore((s) => s.pop)
  useEffect(() => {
    if (!open) return
    pushOverlay()
    return () => popOverlay()
  }, [open, pushOverlay, popOverlay])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const drag = useDragToClose(onClose, open && isMobile)

  if (!open) return null
  if (typeof document === 'undefined') return null

  // ── Mobile: bottom sheet ──────────────────────────────────────
  if (isMobile) {
    return createPortal(
      // Wrapper = sichtbarer Viewport (nicht inset-0): bei offener Tastatur
      // top=offsetTop/height=visualHeight → das bottom-0-Sheet sitzt am
      // sichtbaren Unterrand statt hinter der Tastatur.
      <div
        className="fixed left-0 right-0 z-50"
        style={{ top: viewport.offsetTop, height: viewport.height }}
      >
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss */}
        <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={onClose} />
        <div
          className={cn(
            'app-safe-inline absolute bottom-0 left-0 right-0 bg-bg-elevated rounded-t-2xl flex flex-col animate-slide-up',
            className,
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="side-panel-title"
          // maxHeight am sichtbaren Viewport statt fixem 90vh — bei offener
          // Tastatur schrumpft das Sheet mit, statt dahinter zu liegen.
          style={{
            ...drag.sheetStyle,
            maxHeight: sheetMaxHeight,
            height: mobileFullHeight ? sheetMaxHeight : undefined,
          }}
        >
          {/* Drag handle — auch als drag-target nutzbar zum Schließen */}
          <div
            className="flex justify-center py-2 shrink-0 cursor-grab active:cursor-grabbing"
            {...drag.handlers}
          >
            <div className="w-10 h-1 rounded-full bg-text-tertiary/30" />
          </div>

          {/* Header — bei headerExtra übernimmt dessen Tab-Leiste die
              untere Trennlinie, sonst gäbe es zwei Linien übereinander. */}
          <header
            className={cn(
              'flex shrink-0 items-start justify-between gap-3 px-4 pb-3',
              !headerExtra && 'border-b border-border-subtle',
            )}
            {...drag.handlers}
          >
            <div className="min-w-0 flex-1">
              <h2 id="side-panel-title" className="text-lg font-medium leading-tight truncate">
                {title}
              </h2>
              {description && (
                <p className="mt-0.5 text-xs text-text-secondary truncate">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className={cn(
                mobileHeaderAction && 'order-first',
                'shrink-0 -mr-1 -mt-0.5 p-2.5 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 touch:opacity-100',
              )}
            >
              <X className="h-5 w-5" />
            </button>
            {mobileHeaderAction}
          </header>

          {/* Fixierter Header-Zusatz (Tab-Leiste) — immer sichtbar (D5) */}
          {headerExtra && <div className="shrink-0 px-4">{headerExtra}</div>}

          {/* Content */}
          <div
            ref={bodyRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3"
            // Ohne Footer schließt der Scroll-Body das Sheet nach unten ab —
            // dann braucht ER das Home-Indicator-Padding.
            style={footer ? undefined : { paddingBottom: 'calc(0.75rem + var(--app-safe-bottom))' }}
          >
            {children}
          </div>

          {/* Footer — Safe-Area direkt hier (statt der fragilen
              .animate-slide-up-Kopplung), OHNE display-mode-Query, damit auch
              der Safari-Browser-Tab mit Bottom-Bar abgedeckt ist. */}
          {footer && (
            <footer
              className="flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle px-4 py-3"
              style={{ paddingBottom: 'calc(0.75rem + var(--app-safe-bottom))' }}
            >
              {footer}
            </footer>
          )}
        </div>
      </div>,
      document.body,
    )
  }

  // ── Desktop / tablet: slide from right ────────────────────────
  const desktopPanel = (
    <div
      className={cn(
        'app-safe-right-panel fixed top-0 right-0 bottom-0 z-40 flex flex-col',
        'w-full',
        width,
        'border-l border-border-default bg-bg-elevated shadow-2xl',
        'animate-slide-in-right',
        className,
      )}
      role="dialog"
      aria-modal="false"
      aria-labelledby="side-panel-title"
    >
      <header
        className={cn(
          'flex shrink-0 items-start justify-between gap-3 px-4 py-3',
          !headerExtra && 'border-b border-border-subtle',
        )}
      >
        <div className="min-w-0 flex-1">
          <h2 id="side-panel-title" className="text-lg font-medium leading-tight truncate">
            {title}
          </h2>
          {description && (
            <p className="mt-0.5 text-xs text-text-secondary truncate">{description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="shrink-0 -mr-1 -mt-0.5 p-2.5 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 touch:opacity-100"
        >
          <X className="h-5 w-5" />
        </button>
      </header>
      {headerExtra && <div className="shrink-0 px-4">{headerExtra}</div>}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3">{children}</div>
      {footer && (
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle px-4 py-3">
          {footer}
        </footer>
      )}
    </div>
  )

  if (!desktopBackdrop) return createPortal(desktopPanel, document.body)
  return createPortal(
    <div className="fixed inset-0 z-40">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss */}
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={onClose} />
      {desktopPanel}
    </div>,
    document.body,
  )
}
